import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Loader2, AlertCircle, Clock, X, Type, Sparkles } from 'lucide-react';
import toast from 'react-hot-toast';
import { useThemeStore } from '../../store/themeStore';
import { mediaAPI, aiAPI, formatBytes, getErrorMessage } from '../../services/api';
import type { UrlMedia } from './types';

interface Props {
  urlMediaItems: UrlMedia[];
  setUrlMediaItems: React.Dispatch<React.SetStateAction<UrlMedia[]>>;
}

export const UrlImportTab: React.FC<Props> = ({ urlMediaItems, setUrlMediaItems }) => {
  const { theme } = useThemeStore();
  const { t } = useTranslation();
  const [urlInput, setUrlInput] = useState('');
  const [isUrlValidating, setIsUrlValidating] = useState(false);
  const [editingAltId, setEditingAltId] = useState<string | null>(null);
  // Per-item flag so the Sparkles button can show a spinner while
  // the vision model resolves a description.
  const [describingIds, setDescribingIds] = useState<Set<string>>(new Set());

  const hasMedia = urlMediaItems.length > 0;

  const handleAutoDescribe = async (item: UrlMedia) => {
    if (!item.mediaId) {
      toast.error(
        t(
          'compose_alt_ai_needs_media',
          'Upload must finish before AI can describe this image.'
        )
      );
      return;
    }
    setDescribingIds((prev) => new Set(prev).add(item.id));
    try {
      const { data } = await aiAPI.describeImage(item.mediaId);
      const description =
        (data && (data.description || data.alt_text)) || '';
      if (description) {
        setUrlMediaItems((prev) =>
          prev.map((it) => (it.id === item.id ? { ...it, altText: description } : it))
        );
        toast.success(t('compose_alt_ai_generated', 'Alt text generated'));
      } else {
        toast.error(
          t('compose_alt_ai_empty', 'Model returned no description')
        );
      }
    } catch (err: unknown) {
      toast.error(
        getErrorMessage(
          err,
          t('compose_alt_ai_failed', 'Failed to generate alt text')
        )
      );
    } finally {
      setDescribingIds((prev) => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
    }
  };

  const handleUrlImport = async () => {
    const trimmed = urlInput.trim();
    if (!trimmed) return;

    // Only allow one media item — clear any existing before importing
    setUrlMediaItems([]);
    setIsUrlValidating(true);
    const itemId = Math.random().toString(36).substring(7);

    try {
      const newItem: UrlMedia = {
        id: itemId,
        url: trimmed,
        filename: t('compose_validating', 'Validating...'),
        contentType: '',
        type: 'image',
        status: 'uploading',
        progress: 0,
      };
      setUrlMediaItems((prev) => [...prev, newItem]);
      setUrlInput('');
      setIsUrlValidating(false);

      const result = await mediaAPI.uploadFromUrl(trimmed, (loaded, total) => {
        const pct = total > 0 ? Math.round((loaded / total) * 100) : 0;
        setUrlMediaItems((prev) =>
          prev.map((item) =>
            item.id === itemId
              ? { ...item, progress: pct, loaded, total, filename: item.filename === t('compose_validating', 'Validating...') ? trimmed.split('/').pop() || 'media' : item.filename }
              : item
          )
        );
      });

      const data = result.data;
      setUrlMediaItems((prev) =>
        prev.map((item) =>
          item.id === itemId
            ? {
                ...item,
                status: 'done',
                progress: 100,
                mediaId: data.media_id,
                filename: data.filename || trimmed.split('/').pop() || 'media',
                contentType: data.media_type || '',
                type: data.media_type === 'video' ? 'video' : 'image',
              }
            : item
        )
      );
      toast.success(t('compose_media_imported_url', 'Media imported from URL'));
    } catch (err: unknown) {
      const message = getErrorMessage(err, t('compose_failed_import_media', 'Failed to import media'));
      setUrlMediaItems((prev) =>
        prev.map((item) =>
          item.id === itemId ? { ...item, status: 'error', error: message } : item
        )
      );
      setIsUrlValidating(false);
      toast.error(message);
    }
  };

  const removeUrlMedia = (id: string) => {
    setUrlMediaItems((prev) => prev.filter((item) => item.id !== id));
  };

  const updateAltText = (id: string, altText: string) => {
    setUrlMediaItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, altText } : item))
    );
  };

  return (
    <>
      <div className="space-y-3">
        <div className="flex gap-2">
          <input
            type="url"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleUrlImport(); }}
            placeholder={t('compose_paste_url', 'Paste image or video URL (Drive, Dropbox, etc.')}
            className="flex-1 px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:border-transparent"
            style={{ outlineColor: theme.hex.primaryRing }}
          />
          <button
            onClick={handleUrlImport}
            disabled={!urlInput.trim() || isUrlValidating}
            className="flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-medium text-white transition-all disabled:bg-gray-300"
            style={{ backgroundColor: !urlInput.trim() || isUrlValidating ? undefined : theme.hex.primary }}
          >
            {isUrlValidating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            {t('compose_import_button', 'Import')}
          </button>
        </div>
        <p className="text-xs text-gray-400">
          {hasMedia ? t('compose_url_import_replace', 'Paste a direct link to an image or video. Importing a new URL will replace the current media.') : t('compose_url_import_fetch', 'Paste a direct link to an image or video. We\u2019ll fetch it and store a copy for your post.')}
        </p>
      </div>

      {urlMediaItems.length > 0 && (
        <div className="mt-4 space-y-2.5">
          {urlMediaItems.map((item) => (
            <div key={item.id} className="p-3 bg-gray-50 rounded-lg border border-gray-100">
              <div className="flex items-center gap-3">
                {/* Thumbnail preview for completed media, icon for in-progress */}
                <div className="flex-shrink-0">
                  {item.status === 'done' && item.url && item.type === 'image' ? (
                    <img
                      src={item.url}
                      alt={item.altText || item.filename || t('compose_media_fallback', 'Media')}
                      className="h-12 w-12 rounded-md object-cover border border-gray-200"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                  ) : item.status === 'done' && item.type === 'video' ? (
                    <div className="h-12 w-12 rounded-md bg-gray-200 border border-gray-200 flex items-center justify-center">
                      <span className="text-lg">▶</span>
                    </div>
                  ) : item.status === 'uploading' ? (
                    <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
                  ) : item.status === 'error' ? (
                    <AlertCircle className="h-5 w-5 text-red-500" />
                  ) : (
                    <Clock className="h-5 w-5 text-gray-400" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-700 truncate">{item.filename || item.url}</p>
                  {item.status === 'uploading' && (
                    <div className="mt-1.5">
                      <div className="flex items-center justify-between mb-0.5">
                        <span className="text-[10px] text-gray-500">
                          {item.loaded !== undefined && item.total
                            ? `${formatBytes(item.loaded)} / ${formatBytes(item.total)}`
                            : `${item.progress}%`}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-1.5">
                        <div
                          className="h-1.5 rounded-full transition-all"
                          style={{ width: `${item.progress}%`, backgroundColor: theme.hex.primary }}
                        />
                      </div>
                    </div>
                  )}
                  {item.status === 'error' && <p className="text-[10px] text-red-500 mt-0.5">{item.error}</p>}
                  {item.status === 'done' && (
                    <div className="flex items-center gap-2 mt-0.5">
                      <p className="text-[10px] text-emerald-600">{t('compose_ready', 'Ready')}</p>
                      {item.altText && (
                        <span className="text-[8px] bg-gray-200 text-gray-500 px-1 py-0.5 rounded font-medium">{t('comp_alt_badge', 'ALT')}</span>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {/* Alt text toggle — only for completed image imports */}
                  {item.status === 'done' && item.type === 'image' && (
                    <button
                      onClick={() => setEditingAltId(editingAltId === item.id ? null : item.id)}
                      className={`p-1 rounded transition-colors ${
                        editingAltId === item.id || item.altText
                          ? 'text-blue-500 bg-blue-50'
                          : 'text-gray-400 hover:text-gray-600'
                      }`}
                      title={t('compose_edit_alt_text', 'Edit alt text')}
                    >
                      <Type className="h-3.5 w-3.5" />
                    </button>
                  )}
                  <button
                    onClick={() => removeUrlMedia(item.id)}
                    className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Inline alt text editor */}
              {editingAltId === item.id && (
                <div className="mt-2 pt-2 border-t border-gray-100">
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={item.altText || ''}
                      onChange={(e) => updateAltText(item.id, e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') setEditingAltId(null); }}
                      placeholder={t('compose_alt_text_placeholder', 'Describe this image for accessibility...')}
                      className="flex-1 px-2.5 py-1.5 text-[11px] border border-gray-200 rounded-md bg-white focus:ring-1"
                      style={{ outlineColor: theme.hex.primaryRing }}
                    />
                    {item.type === 'image' && (
                      <button
                        type="button"
                        onClick={() => handleAutoDescribe(item)}
                        disabled={!item.mediaId || describingIds.has(item.id)}
                        aria-busy={describingIds.has(item.id)}
                        title={
                          !item.mediaId
                            ? t(
                                'compose_alt_ai_disabled',
                                'Finish upload to enable AI description'
                              )
                            : t('compose_alt_ai_tip', 'Auto-describe with AI')
                        }
                        className="flex items-center gap-1 px-2 py-1.5 rounded-md text-[11px] font-medium border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        {describingIds.has(item.id) ? (
                          <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
                        ) : (
                          <Sparkles className="h-3 w-3" aria-hidden="true" />
                        )}
                        {t('compose_alt_ai_button', 'Describe')}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );
};
